/**
 * Hot cocoa bar ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hotCocoaBarIdeasPack: ToilePack = {
    slug: 'hot-cocoa-bar-ideas',
    category: 'season',
    name: 'Hot cocoa bar ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'cart', label: 'Cart', subject: 'a hot cocoa bar on a bar cart'},
        {key: 'counter', label: 'Counter', subject: 'a cocoa bar on a kitchen counter'},
        {key: 'table', label: 'Table', subject: 'a cocoa bar table with toppings'},
    ],
    styles: [
        {key: 'farmhouse', label: 'Farmhouse', style: 'with white enamel mugs on hooks, a galvanised tray, wooden crates, a short fir garland and pinecones, soft daylight from a window at camera-left, a warm, bright balance with soft shadow', palette: 'White enamel, raw wood, candy-stripe red and fir green.', framing: 'Seen straight on at counter height, the whole setup in view.'},
        {key: 'candycane', label: 'Candy cane', style: 'in a playful candy-cane theme with red-and-white striped straws, peppermint bark, glass jars of mini marshmallows and sprinkles and a red bead garland, bright overcast daylight falling frontal, a high, cheerful balance', palette: 'Red, white and pink with touches of silver.', framing: 'Seen from a slight three-quarter angle, the toppings in rows leading back.'},
        {key: 'fireside', label: 'Fireside', style: 'as a cozy evening setup by the fire with a copper kettle, a chunky knit throw and glass mugs of cocoa topped with cream, warm firelight glowing from camera-right with the room dim, a dark, warm balance', palette: 'Copper, chocolate brown, cream and deep green.', framing: 'Seen at a low angle close to the setup, the far room falling into shadow.'},
    ],
    directions: ['kids party', 'coffee bar', 'office party'],
    directionHint: 'try: kids party',
    seo: {
        title: 'Hot Cocoa Bar Ideas: Cart, Counter, Table',
        metaDescription: 'Nine photos of a hot cocoa bar on a cart, a counter and a table, in farmhouse, candy cane and fireside styles, for a Christmas party.',
        h1: 'Hot cocoa bar ideas: cart, counter, table',
        tagline: 'Three cocoa bars, each set up in a farmhouse, a candy cane and a fireside style.',
        intro: 'Hot cocoa bars for a Christmas gathering: a bar cart, a kitchen counter and a table with toppings. The three looks are farmhouse, candy cane and fireside, each with mugs, toppings and cocoa. Use them to plan a cocoa bar for a party. Type a twist, such as kids party or coffee bar, and the bar is asked to match it.',
        useCases: [
            'setting up a cocoa bar for a party',
            "a kids' party table",
            'a corner of the home for winter guests',
            'a treat table for an office party',
        ],
        faq: [
            {q: 'What is on the bars?', a: 'Mugs, jars of marshmallows, candy canes, sprinkles and whipped cream, with a few decorations around them.'},
            {q: 'What are the three styles?', a: 'Farmhouse uses white enamel mugs, wooden crates and a fir garland. Candy cane uses red and white striped straws and peppermint bark. Fireside is a copper kettle and chunky knit throw by the fire.'},
            {q: 'Can I ask for a different setup?', a: 'Yes. Type a twist, such as a coffee bar, and the page asks for a setup that follows it.'},
        ],
    },
}
